import { useMemo, useState } from 'react'
import { FlaskConical, Search, Ticket, X } from 'lucide-react'
import { ButtonLink, EmptyState, Input, OfferSkeleton, Spinner } from '@/components/ui'
import { BookletHeader } from '@/components/BookletHeader'
import { OfferCard } from '@/components/offers/OfferCard'
import { OfferDetail } from '@/components/offers/OfferDetail'
import { FlipbookOverlay } from '@/components/offers/FlipbookOverlay'
import { useBooklets } from '@/hooks/useBooklets'
import { useMyCodes } from '@/hooks/useMyCodes'
import { useMyRedemptions, useOffers } from '@/hooks/useOffers'
import { formatDate, normalizeForSearch } from '@/lib/format'
import { describeOffer } from '@shared/offers'
import {
  CATEGORY_LABELS,
  currentBooklet,
  usableBooklets,
  type Category,
  type Offer,
} from '@shared/model'
import { cn } from '@/lib/cn'
import { QueryError } from '@/components/QueryError'

export function BookletPage() {
  const { booklets, loading: bookletsLoading, error: bookletsError } = useBooklets()
  const { codesByBooklet, loading: codesLoading, error: codesError } = useMyCodes()

  const [selectedBookletId, setSelectedBookletId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<Category | 'all'>('all')
  const [openOffer, setOpenOffer] = useState<Offer | null>(null)
  const [flipbookOpen, setFlipbookOpen] = useState(false)

  /**
   * Perioder användaren äger ett häfte i och som fortfarande är i bruk.
   *
   * Utgångna perioder hör hemma under Mitt konto som historik, inte här med
   * sina erbjudanden – de går ändå inte att lösa in.
   */
  const allOwned = useMemo(
    () => booklets.filter((booklet) => (codesByBooklet[booklet.id]?.length ?? 0) > 0),
    [booklets, codesByBooklet],
  )
  const ownedBooklets = useMemo(() => usableBooklets(allOwned), [allOwned])

  /** Senast utgångna häftet, bara för att kunna säga vart det tagit vägen. */
  const lastExpired = useMemo(
    () => allOwned.find((item) => !ownedBooklets.includes(item)) ?? null,
    [allOwned, ownedBooklets],
  )

  const booklet = useMemo(() => {
    if (selectedBookletId) {
      const chosen = ownedBooklets.find((item) => item.id === selectedBookletId)
      if (chosen) return chosen
    }
    return currentBooklet(ownedBooklets)
  }, [ownedBooklets, selectedBookletId])

  const { data: offers, loading: offersLoading } = useOffers(booklet?.id ?? null)
  const { usedByOffer, totalSavedSek } = useMyRedemptions(booklet?.id ?? null)

  /** Antal häften kontot äger i perioden – varje erbjudande får ett uttag per häfte. */
  const owned = booklet ? (codesByBooklet[booklet.id]?.length ?? 0) : 0

  const remainingFor = (offer: Offer) => owned - (usedByOffer[offer.id] ?? 0)

  const visibleOffers = useMemo(() => {
    const needle = normalizeForSearch(search)
    return offers.filter((offer) => {
      if (category !== 'all' && offer.companyCategory !== category) return false
      if (!needle) return true
      const haystack = normalizeForSearch(
        [
          offer.title,
          offer.companyName,
          offer.description ?? '',
          describeOffer(offer.offerTerms),
        ].join(' '),
      )
      return haystack.includes(needle)
    })
  }, [offers, search, category])

  /** Bara kategorier som faktiskt finns i häftet visas som filter. */
  /*
   * Vad hela häftet är värt, som schablonvärdena summerar till.
   *
   * Räknas på alla erbjudanden, inte de som filtret råkar visa – annars
   * skulle häftets värde krympa medan man söker.
   */
  const bookletValueSek = useMemo(
    () => offers.reduce((sum, offer) => sum + (offer.estimatedValueSek ?? 0), 0),
    [offers],
  )

  const availableCategories = useMemo(() => {
    const found = new Set<Category>()
    for (const offer of offers) found.add(offer.companyCategory)
    return [...found].sort((a, b) => CATEGORY_LABELS[a].localeCompare(CATEGORY_LABELS[b], 'sv'))
  }, [offers])



  if (bookletsLoading || codesLoading) return <Spinner label="Hämtar ditt häfte…" />

  // Ett fel får aldrig presenteras som ett tomt häfte.
  if (bookletsError || codesError) return <QueryError error={bookletsError ?? codesError} />

  if (!booklet) {
    /*
     * Den som precis haft ett häfte får ett annat svar än den som aldrig
     * haft ett. "Inget häfte aktiverat än" till någon som använde sitt i
     * förra veckan läser som att appen tappat bort det, och frågan "vart
     * tog mitt häfte vägen?" är den som annars ringer lagledaren.
     */
    if (lastExpired) {
      return (
        <EmptyState
          icon={<Ticket size={48} aria-hidden />}
          title="Perioden är slut"
          description={`${lastExpired.periodName} gick ut ${formatDate(lastExpired.endDate)} och erbjudandena går inte längre att använda. Vad du sparade under perioden finns kvar under Mitt konto.`}
          action={<ButtonLink to="/konto">Se vad du sparade</ButtonLink>}
        />
      )
    }

    return (
      <EmptyState
        icon={<Ticket size={48} aria-hidden />}
        title="Inget häfte aktiverat än"
        description="Har du köpt ett Lirahäfte av en spelare? Aktivera koden på lappen så öppnas alla erbjudanden här."
        action={<ButtonLink to="/mina-koder">Aktivera en kod</ButtonLink>}
      />
    )
  }

  return (
    <div className="space-y-4">
      {booklet.isTest && (
        <p className="flex items-start gap-2 rounded-card border border-warning/40 bg-warning-soft px-3.5 py-2.5 text-sm font-semibold text-warning">
          <FlaskConical size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>Testhäfte. Innehållet är på låtsas och räknas inte in i statistiken.</span>
        </p>
      )}

      <BookletHeader
        booklet={booklet}
        totalSavedSek={totalSavedSek}
        offerCount={offers.length}
        bookletValueSek={bookletValueSek}
        onOpenFlipbook={() => setFlipbookOpen(true)}
      />

      {/* Äger man häften i flera perioder går det att växla mellan dem. */}
      {ownedBooklets.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {ownedBooklets.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedBookletId(item.id)}
              className={cn(
                'rounded-pill border px-3.5 py-2 text-sm font-semibold transition-colors',
                item.id === booklet.id
                  ? 'border-brand bg-brand text-on-brand'
                  : 'border-border bg-surface text-muted hover:text-ink',
              )}
            >
              {item.periodName}
            </button>
          ))}
        </div>
      )}

      {/* Sök och bläddra på samma rad. Bläddringsknappen låg tidigare i full
          bredd och mättat gult, vilket dels sköt ned första erbjudandet under
          vikningen på en liten skärm, dels gjorde gult till sidans blickfång
          i stället för rabatterna. Gult är nu sparat till inlösen. */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={18}
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-faint"
          />
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Sök företag eller erbjudande"
            aria-label="Sök i häftet"
            className="pr-11 pl-11"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Rensa sökningen"
              className="tap-safe absolute top-1/2 right-1.5 -translate-y-1/2 rounded-pill p-2.5 text-muted hover:text-ink"
            >
              <X size={16} aria-hidden />
            </button>
          )}
        </div>

      </div>

      {/* Kategorifilter */}
      {availableCategories.length > 1 && (
        <div className="-mx-4 overflow-x-auto px-4 no-scrollbar">
          <div className="flex w-max gap-2 pb-1">
            <CategoryChip
              active={category === 'all'}
              onClick={() => setCategory('all')}
              label={`Alla (${offers.length})`}
            />
            {availableCategories.map((item) => (
              <CategoryChip
                key={item}
                active={category === item}
                onClick={() => setCategory(item)}
                label={CATEGORY_LABELS[item]}
              />
            ))}
          </div>
        </div>
      )}

      {offersLoading ? (
        /* Skelett i stället för spinner: här vet vi exakt vilken form som
           kommer, och sidan ovanför är redan på plats. Då är en snurra bara
           ett hål i en färdig layout. */
        <OfferSkeleton />
      ) : visibleOffers.length === 0 ? (
        <EmptyState
          icon={<Search size={44} aria-hidden />}
          title="Inga träffar"
          description={
            search
              ? `Ingenting matchar "${search}". Prova ett kortare sökord.`
              : 'Det finns inga erbjudanden i den här kategorin just nu.'
          }
        />
      ) : (
        <ul className="space-y-2.5">
          {visibleOffers.map((offer) => (
            <li key={offer.id}>
              <OfferCard
                brandColor={offer.companyBrandColor}
                offer={offer}
                remaining={remainingFor(offer)}
                owned={owned}
                onClick={() => setOpenOffer(offer)}
              />
            </li>
          ))}
        </ul>
      )}

      {flipbookOpen && (
        <FlipbookOverlay
          offers={visibleOffers.length > 0 ? visibleOffers : offers}
          owned={owned}
          remainingFor={remainingFor}
          periodName={booklet.periodName}
          onOpenOffer={setOpenOffer}
          onClose={() => setFlipbookOpen(false)}
        />
      )}

      <OfferDetail
        offer={openOffer}
        onClose={() => setOpenOffer(null)}
        remaining={openOffer ? remainingFor(openOffer) : 0}
        owned={owned}
        bookletStatus={booklet.status}
        bookletStartDate={booklet.startDate}
        bookletEndDate={booklet.endDate}
      />
    </div>
  )
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'tap-safe rounded-pill border px-3.5 py-2 text-sm font-semibold whitespace-nowrap',
        'transition-colors',
        active
          ? 'border-brand bg-brand text-on-brand'
          : 'border-border bg-surface text-muted hover:text-ink',
      )}
    >
      {label}
    </button>
  )
}
